import {
  changePasswordInput,
  localNameInput,
  PASSKEY_NAME_MAX,
  passkeyName,
  profileInput,
} from "@bt/domain";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeftIcon,
  BanIcon,
  CloudIcon,
  FingerprintIcon,
  ImageUpIcon,
  KeyRoundIcon,
  MonitorIcon,
  MoonIcon,
  PencilIcon,
  SmartphoneIcon,
  SunIcon,
  Trash2Icon,
  TriangleAlertIcon,
  UserXIcon,
  XCircleIcon,
} from "lucide-react";
import { type ReactNode, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Avatar } from "@/components/app/avatar";
import { Field } from "@/components/app/field";
import { LanguagePicker } from "@/components/app/language-picker";
import { Logo } from "@/components/app/logo";
import { useSignOut } from "@/components/app/user-menu";
import { ConfirmDialog } from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Segmented } from "@/components/ui/misc";
import { toLanguage } from "@/i18n";
import {
  encryptsGarages,
  hasLocalUsers,
  hasPasskeys,
  type Me,
  signInUrl,
  useMe,
} from "@/lib/account";
import { errorCode, errorMessage } from "@/lib/errors";
import { useFormat } from "@/lib/format";
import { navigate } from "@/lib/pages";
import { useChangePreferences } from "@/lib/preferences";
import { useSystemInfo } from "@/lib/system";
import { type ThemeChoice, useTheme } from "@/lib/theme";
import { useTRPC, useTRPCClient } from "@/lib/trpc";
import { uploadAvatar } from "@/lib/upload";
import { AccountRecoveryKeyCard } from "../keys/keys";
import { AuthCard } from "./account-app";
import { EndDemoDialog } from "./demo";
import { useAddPasskey } from "./passkey-offer";
import { PinFields, pinErrors } from "./pin-fields";
import { ShareUsageSetting } from "./share-usage";
import { FormError, fieldErrors, NewPasswordFields, newPasswordErrors } from "./sign-in";
import { SecondFactorField, TwoFactorCard } from "./two-factor";

/**
 * /account/profile: name, picture, password, passkeys, and deleting the account. A visitor of the public
 * demo (ADR 0012) has only a name, and ends the demo instead. A user of the desktop app (ADR 0013)
 * has an optional PIN instead of a password; admins delete users.
 */
export function ProfilePage() {
  const { t } = useTranslation();
  const { data: me, isPending } = useMe();

  useEffect(() => {
    if (isPending) return;
    if (!me) window.location.replace(signInUrl());
    else if (me.status !== "active") window.location.replace("/account/restricted");
  }, [me, isPending]);
  if (me?.status !== "active") return null;

  return (
    <div className="min-h-dvh bg-background">
      <header className="mx-auto flex max-w-2xl items-center justify-between gap-3 px-4 pt-[max(1rem,env(safe-area-inset-top))] pb-2 sm:px-6">
        <a
          href="/"
          className="rounded-lg outline-none focus-visible:ring-2 focus-visible:ring-ring/40"
        >
          <Logo />
        </a>
        <Button asChild variant="ghost" size="sm">
          <a href="/">
            <ArrowLeftIcon />
            {t("account.backToGarages")}
          </a>
        </Button>
      </header>
      <main className="mx-auto flex max-w-2xl flex-col gap-6 px-4 pt-4 pb-16 sm:px-6">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">{t("account.profileTitle")}</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {me.demo
              ? t("demo.profileBody")
              : hasLocalUsers()
                ? t("account.profileBodyUsers")
                : t("account.profileBody")}
          </p>
        </div>
        <ProfileCard me={me} />
        <PreferencesCard />
        {me.demo ? (
          <EndDemoCard expiresAt={me.demo.expiresAt} />
        ) : hasLocalUsers() ? (
          <PinCard hasPin={me.pin === true} />
        ) : (
          <>
            <PasswordCard />
            {hasPasskeys() ? <PasskeysCard /> : null}
            <Section title={t("twoFactor.title")} body={t("twoFactor.body")}>
              <TwoFactorCard />
            </Section>
            {encryptsGarages() ? (
              <Section title={t("keys.profileTitle")} body={t("keys.accountKeyBody")}>
                <AccountRecoveryKeyCard />
              </Section>
            ) : null}
            <DeleteCard />
          </>
        )}
      </main>
    </div>
  );
}

function Section({
  id,
  title,
  body,
  children,
}: {
  id?: string;
  title: string;
  body?: string;
  children: ReactNode;
}) {
  return (
    <Card id={id} className="scroll-mt-4 p-4 sm:p-5">
      <h2 className="text-[15px] font-semibold tracking-tight">{title}</h2>
      {body ? <p className="mt-0.5 text-sm text-muted-foreground">{body}</p> : null}
      <div className="mt-4">{children}</div>
    </Card>
  );
}

function ProfileCard({ me }: { me: Me }) {
  const { t } = useTranslation();
  const trpc = useTRPC();
  // Pictures are uploads, which the public demo doesn't take.
  const picture = useSystemInfo()?.capabilities.uploads ?? false;
  const format = useFormat();
  const queryClient = useQueryClient();
  const update = useMutation({
    ...trpc.account.updateProfile.mutationOptions(),
    meta: { silent: true },
  });
  const removeAvatar = useMutation(trpc.account.removeAvatar.mutationOptions());
  const fileInput = useRef<HTMLInputElement>(null);
  const [firstName, setFirstName] = useState(me.firstName ?? "");
  const [lastName, setLastName] = useState(me.lastName ?? "");
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [uploading, setUploading] = useState(false);

  return (
    <Section title={picture ? t("account.nameAndPicture") : t("common.name")}>
      <div className="flex flex-col gap-5">
        <div className="flex items-center gap-4">
          <Avatar user={me} className="size-16 text-lg" />
          <div className={picture ? "flex flex-wrap gap-2" : "hidden"}>
            <Button
              variant="outline"
              size="sm"
              disabled={uploading}
              onClick={() => fileInput.current?.click()}
            >
              <ImageUpIcon />
              {t("account.uploadPicture")}
            </Button>
            {me.avatarUrl ? (
              <Button variant="ghost" size="sm" onClick={() => removeAvatar.mutate()}>
                <Trash2Icon />
                {t("account.removePicture")}
              </Button>
            ) : null}
            <input
              ref={fileInput}
              type="file"
              accept="image/png,image/jpeg,image/webp"
              className="hidden"
              data-testid="avatar-file"
              onChange={async (event) => {
                const file = event.target.files?.[0];
                event.target.value = "";
                if (!file) return;
                setUploading(true);
                try {
                  await uploadAvatar(file);
                  await queryClient.invalidateQueries();
                  toast.success(t("account.pictureSaved"));
                } catch (error) {
                  toast.error(errorMessage(error, t));
                } finally {
                  setUploading(false);
                }
              }}
            />
          </div>
        </div>
        <form
          className="flex flex-col gap-4"
          onSubmit={async (event) => {
            event.preventDefault();
            // On the desktop app a first name is enough (ADR 0013).
            const schema = hasLocalUsers() ? localNameInput : profileInput;
            const parsed = schema.safeParse({ firstName, lastName });
            if (!parsed.success) {
              setErrors(fieldErrors(parsed.error.issues));
              return;
            }
            setErrors({});
            await update.mutateAsync(parsed.data);
            toast.success(t("account.profileSaved"));
          }}
        >
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label={t("account.firstName")} htmlFor="profile-first" error={errors.firstName}>
              <Input
                id="profile-first"
                autoComplete="given-name"
                value={firstName}
                aria-invalid={errors.firstName ? true : undefined}
                onChange={(e) => setFirstName(e.target.value)}
              />
            </Field>
            <Field
              label={t("account.lastName")}
              htmlFor="profile-last"
              error={errors.lastName}
              optional={hasLocalUsers()}
            >
              <Input
                id="profile-last"
                autoComplete="family-name"
                value={lastName}
                aria-invalid={errors.lastName ? true : undefined}
                onChange={(e) => setLastName(e.target.value)}
              />
            </Field>
          </div>
          {me.email ? (
            <Field label={t("account.email")} htmlFor="profile-email" hint={t("account.emailHint")}>
              <Input id="profile-email" value={me.email} readOnly />
            </Field>
          ) : null}
          <dl className="grid grid-cols-2 gap-3 text-sm">
            <div>
              <dt className="text-xs text-muted-foreground">{t("account.createdOn")}</dt>
              <dd>{format.date(me.createdAt)}</dd>
            </div>
            <div>
              <dt className="text-xs text-muted-foreground">{t("account.lastSignIn")}</dt>
              <dd>{format.dateTime(me.lastLoginAt)}</dd>
            </div>
          </dl>
          <Button type="submit" className="self-start" disabled={update.isPending}>
            {t("common.saveChanges")}
          </Button>
        </form>
      </div>
    </Section>
  );
}

function PasswordCard() {
  const { t } = useTranslation();
  const trpc = useTRPC();
  const change = useMutation({
    ...trpc.account.changePassword.mutationOptions(),
    meta: { silent: true },
  });
  const [current, setCurrent] = useState("");
  const [password, setPassword] = useState("");
  const [repeat, setRepeat] = useState("");
  const [errors, setErrors] = useState<Record<string, string | undefined>>({});
  const [serverError, setServerError] = useState<string | null>(null);

  return (
    <Section title={t("account.changePassword")} body={t("account.changePasswordBody")}>
      <form
        className="flex flex-col gap-4"
        onSubmit={async (event) => {
          event.preventDefault();
          const problems = { ...(newPasswordErrors(password, repeat) ?? {}) } as Record<
            string,
            string | undefined
          >;
          if (!changePasswordInput.shape.current.safeParse(current).success) {
            problems.current = "validation.required";
          }
          setErrors(problems);
          if (Object.values(problems).some(Boolean)) return;
          setServerError(null);
          try {
            await change.mutateAsync({ current, next: password });
            setCurrent("");
            setPassword("");
            setRepeat("");
            toast.success(t("account.passwordChanged"));
          } catch (error) {
            setServerError(errorMessage(error, t));
          }
        }}
      >
        <Field
          label={t("account.currentPassword")}
          htmlFor="password-current"
          error={errors.current}
        >
          <Input
            id="password-current"
            type="password"
            autoComplete="current-password"
            value={current}
            aria-invalid={errors.current ? true : undefined}
            onChange={(e) => setCurrent(e.target.value)}
          />
        </Field>
        <NewPasswordFields
          idPrefix="password-new"
          password={password}
          repeat={repeat}
          onPassword={setPassword}
          onRepeat={setRepeat}
          errors={errors}
        />
        <FormError message={serverError} />
        <Button type="submit" className="self-start" disabled={change.isPending}>
          {t("account.changePassword")}
        </Button>
      </form>
    </Section>
  );
}

/**
 * Their passkeys (ADR 0011): one per device or password manager, added, renamed and deleted here.
 * Adding one on a session older than a day asks for the password first.
 */
function PasskeysCard() {
  const { t } = useTranslation();
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const format = useFormat();
  const passkeys = useQuery(trpc.account.passkeys.list.queryOptions());
  const add = useAddPasskey();
  const rename = useMutation({
    ...trpc.account.passkeys.rename.mutationOptions(),
    meta: { silent: true },
  });
  const remove = useMutation(trpc.account.passkeys.remove.mutationOptions());
  const [adding, setAdding] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [editing, setEditing] = useState<{ id: string; name: string } | null>(null);
  const [nameError, setNameError] = useState<string | undefined>();
  const [deleting, setDeleting] = useState<{ id: string; name: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const list = passkeys.data ?? [];

  const addPasskey = async () => {
    setError(null);
    setAdding(true);
    try {
      if (await add()) {
        await queryClient.invalidateQueries();
        toast.success(t("account.passkeyAdded"));
      }
    } catch (e) {
      if (errorCode(e) === "PASSWORD_CONFIRM_NEEDED") setConfirming(true);
      else setError(errorMessage(e, t));
    } finally {
      setAdding(false);
    }
  };

  return (
    <Section title={t("account.passkeys")} body={t("account.passkeysBody")}>
      <div className="flex flex-col gap-4">
        {list.length > 0 ? (
          <ul className="divide-y divide-border rounded-lg border border-border">
            {list.map((passkey) => {
              const name = passkey.name || t("account.passkeyDefaultName");
              return (
                <li key={passkey.id} className="flex items-start gap-3 px-3 py-2.5">
                  <KeyRoundIcon className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
                  {editing?.id === passkey.id ? (
                    <form
                      className="flex min-w-0 flex-1 flex-col gap-2"
                      onSubmit={async (event) => {
                        event.preventDefault();
                        const parsed = passkeyName.safeParse(editing.name);
                        if (!parsed.success) {
                          setNameError(parsed.error.issues[0]?.message);
                          return;
                        }
                        try {
                          await rename.mutateAsync({ id: passkey.id, name: parsed.data });
                          setEditing(null);
                        } catch (e) {
                          setNameError(errorMessage(e, t));
                        }
                      }}
                    >
                      <Field
                        label={t("account.passkeyName")}
                        htmlFor={`passkey-name-${passkey.id}`}
                        error={nameError}
                      >
                        <Input
                          id={`passkey-name-${passkey.id}`}
                          autoFocus
                          maxLength={PASSKEY_NAME_MAX}
                          value={editing.name}
                          aria-invalid={nameError ? true : undefined}
                          onChange={(e) => setEditing({ id: passkey.id, name: e.target.value })}
                        />
                      </Field>
                      <div className="flex gap-2">
                        <Button type="submit" size="sm" disabled={rename.isPending}>
                          {t("common.save")}
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          onClick={() => setEditing(null)}
                        >
                          {t("common.cancel")}
                        </Button>
                      </div>
                    </form>
                  ) : (
                    <>
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-medium">{name}</p>
                        <p className="flex flex-wrap items-center gap-x-2 text-xs text-muted-foreground">
                          <span>
                            {t("account.passkeyCreated", { date: format.date(passkey.createdAt) })}
                          </span>
                          <span className="flex items-center gap-1">
                            {passkey.synced ? (
                              <CloudIcon className="size-3" />
                            ) : (
                              <SmartphoneIcon className="size-3" />
                            )}
                            {passkey.synced
                              ? t("account.passkeySynced")
                              : t("account.passkeyOneDevice")}
                          </span>
                        </p>
                      </div>
                      <Button
                        variant="ghost"
                        size="icon-sm"
                        aria-label={t("account.passkeyRename", { name })}
                        onClick={() => {
                          setNameError(undefined);
                          setEditing({ id: passkey.id, name });
                        }}
                      >
                        <PencilIcon />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon-sm"
                        aria-label={t("account.passkeyDelete", { name })}
                        onClick={() => setDeleting({ id: passkey.id, name })}
                      >
                        <Trash2Icon />
                      </Button>
                    </>
                  )}
                </li>
              );
            })}
          </ul>
        ) : passkeys.isSuccess ? (
          <p className="text-sm text-muted-foreground">{t("account.passkeysEmpty")}</p>
        ) : null}
        <FormError message={error} />
        <Button variant="outline" className="self-start" disabled={adding} onClick={addPasskey}>
          <FingerprintIcon />
          {t("account.passkeyAdd")}
        </Button>
      </div>
      <ConfirmPasswordDialog
        open={confirming}
        onOpenChange={setConfirming}
        onConfirmed={() => {
          setConfirming(false);
          void addPasskey();
        }}
      />
      <ConfirmDialog
        open={deleting !== null}
        onOpenChange={(open) => !open && setDeleting(null)}
        title={t("account.passkeyDeleteTitle", { name: deleting?.name ?? "" })}
        description={t("account.passkeyDeleteBody")}
        confirmLabel={t("common.delete")}
        destructive
        pending={remove.isPending}
        onConfirm={() => {
          if (deleting) {
            remove.mutate(
              { id: deleting.id },
              { onSuccess: () => toast.success(t("account.passkeyDeleted")) },
            );
          }
          setDeleting(null);
        }}
      />
    </Section>
  );
}

/** The password again before adding a passkey, when the sign-in is older than a day. */
function ConfirmPasswordDialog({
  open,
  onOpenChange,
  onConfirmed,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirmed: () => void;
}) {
  const { t } = useTranslation();
  const trpc = useTRPC();
  const confirm = useMutation({
    ...trpc.account.confirmPassword.mutationOptions(),
    meta: { silent: true },
  });
  const [password, setPassword] = useState("");
  const [askCode, setAskCode] = useState(false);
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setPassword("");
      setCode("");
      setAskCode(false);
      setError(null);
    }
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader
          title={t("account.passkeyConfirmTitle")}
          description={t("account.passkeyConfirmBody")}
        />
        <form
          className="contents"
          onSubmit={async (event) => {
            event.preventDefault();
            if (!password) {
              setError(t("validation.required"));
              return;
            }
            try {
              await confirm.mutateAsync({ password, code: askCode ? code : null });
              onConfirmed();
            } catch (e) {
              // Two-factor sign-in (ADR 0011): the code comes with the password.
              if (errorCode(e) === "TWO_FACTOR_REQUIRED" && !askCode) setAskCode(true);
              else setError(errorMessage(e, t));
            }
          }}
        >
          <DialogBody className="flex flex-col gap-4">
            <Field label={t("account.password")} htmlFor="passkey-password">
              <Input
                id="passkey-password"
                type="password"
                autoFocus
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </Field>
            {askCode ? (
              <SecondFactorField id="passkey-code" value={code} onChange={setCode} />
            ) : null}
            <FormError message={error} />
          </DialogBody>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
              {t("common.cancel")}
            </Button>
            <Button type="submit" disabled={confirm.isPending}>
              {t("account.passkeyConfirm")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/** The desktop app's users (ADR 0013): their own optional PIN. */
function PinCard({ hasPin }: { hasPin: boolean }) {
  const { t } = useTranslation();
  const trpc = useTRPC();
  const save = useMutation({ ...trpc.account.setPin.mutationOptions(), meta: { silent: true } });
  const [editing, setEditing] = useState(false);
  const [pin, setPin] = useState("");
  const [repeat, setRepeat] = useState("");
  const [errors, setErrors] = useState<{ pin?: string; repeat?: string }>({});
  const [serverError, setServerError] = useState<string | null>(null);

  const change = async (next: string | null, done: string) => {
    setServerError(null);
    try {
      await save.mutateAsync({ pin: next });
      setEditing(false);
      setPin("");
      setRepeat("");
      toast.success(done);
    } catch (error) {
      setServerError(errorMessage(error, t));
    }
  };

  return (
    <Section title={t("users.pin")} body={hasPin ? t("users.pinWith") : t("users.pinWithout")}>
      {editing ? (
        <form
          className="flex flex-col gap-4"
          onSubmit={(event) => {
            event.preventDefault();
            const problems = pinErrors(pin, repeat);
            setErrors(problems ?? {});
            if (!problems) void change(pin, t("users.pinSaved"));
          }}
        >
          <PinFields
            idPrefix="profile"
            label={t("users.newPin")}
            pin={pin}
            repeat={repeat}
            onPin={setPin}
            onRepeat={setRepeat}
            errors={errors}
          />
          <FormError message={serverError} />
          <div className="flex flex-wrap gap-2">
            <Button type="submit" disabled={save.isPending}>
              {t("users.savePin")}
            </Button>
            <Button type="button" variant="ghost" onClick={() => setEditing(false)}>
              {t("common.cancel")}
            </Button>
          </div>
        </form>
      ) : (
        <div className="flex flex-col gap-3">
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={() => setEditing(true)}>
              <KeyRoundIcon />
              {hasPin ? t("users.changePin") : t("users.setPin")}
            </Button>
            {hasPin ? (
              <Button
                variant="ghost"
                disabled={save.isPending}
                onClick={() => void change(null, t("users.ownPinRemoved"))}
              >
                <Trash2Icon />
                {t("users.removePin")}
              </Button>
            ) : null}
          </div>
          <FormError message={serverError} />
        </div>
      )}
    </Section>
  );
}

function DeleteCard() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  return (
    <Section title={t("account.deleteTitle")} body={t("account.deleteBody")}>
      <Button variant="destructive" onClick={() => setOpen(true)}>
        <UserXIcon />
        {t("account.deleteConfirm")}
      </Button>
      <DeleteAccountDialog open={open} onOpenChange={setOpen} />
    </Section>
  );
}

/** Their language and theme, kept with the account (ADR 0011). */
function PreferencesCard() {
  const { t, i18n } = useTranslation();
  const { theme } = useTheme();
  const { changeLanguage, changeTheme } = useChangePreferences();
  // Garage settings › General links here (#preferences); the page renders after the browser's own jump.
  useEffect(() => {
    if (window.location.hash === "#preferences") {
      document.getElementById("preferences")?.scrollIntoView({ block: "start" });
    }
  }, []);
  const icon = (Icon: typeof SunIcon, label: string) => (
    <span className="flex items-center gap-1.5">
      <Icon className="size-3.5" />
      {label}
    </span>
  );
  return (
    <Section id="preferences" title={t("account.preferences")} body={t("account.preferencesBody")}>
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-1.5">
          <span className="text-sm font-medium">{t("settings.language")}</span>
          <LanguagePicker
            aria-label={t("settings.language")}
            value={toLanguage(i18n.language)}
            onChange={changeLanguage}
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <span className="text-sm font-medium">{t("settings.theme")}</span>
          <Segmented<ThemeChoice>
            aria-label={t("settings.theme")}
            value={theme}
            onChange={changeTheme}
            options={[
              { value: "system", label: icon(MonitorIcon, t("settings.themeSystem")) },
              { value: "light", label: icon(SunIcon, t("settings.themeLight")) },
              { value: "dark", label: icon(MoonIcon, t("settings.themeDark")) },
            ]}
          />
        </div>
        <ShareUsageSetting />
      </div>
    </Section>
  );
}

/** A visitor's "End demo now": the demo garage goes with everything in it. */
function EndDemoCard({ expiresAt }: { expiresAt: string }) {
  const { t } = useTranslation();
  const format = useFormat();
  const [open, setOpen] = useState(false);
  return (
    <Section title={t("demo.end")} body={t("demo.deletedOn", { date: format.dateTime(expiresAt) })}>
      <Button variant="destructive" onClick={() => setOpen(true)}>
        <XCircleIcon />
        {t("demo.end")}
      </Button>
      <EndDemoDialog open={open} onOpenChange={setOpen} />
    </Section>
  );
}

/** "Delete my account", confirmed with the password; from the profile or the restricted page. */
export function DeleteAccountDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useTranslation();
  const trpc = useTRPC();
  const client = useTRPCClient();
  const queryClient = useQueryClient();
  const [pending, setPending] = useState(false);
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  // What it does to their garages (ADR 0011): refused as the last garage admin of others, and a
  // garage they are alone in goes with the account.
  const { data: plan } = useQuery({ ...trpc.account.deletionPlan.queryOptions(), enabled: open });
  const blocked = plan?.blocked ?? [];
  const deleted = plan?.deleted ?? [];

  useEffect(() => {
    if (open) {
      setPassword("");
      setError(null);
    }
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader
          title={t("account.deleteConfirmTitle")}
          description={t("account.deleteBody")}
        />
        <form
          className="contents"
          onSubmit={async (event) => {
            event.preventDefault();
            if (!password) {
              setError(t("validation.required"));
              return;
            }
            setPending(true);
            try {
              // Through the client, so no query reloads in between: a reload of `me` would race
              // this page's "signed out" redirect, as would a full load.
              await client.account.deleteMyAccount.mutate({ password });
              queryClient.setQueryData(trpc.account.me.queryKey(), null);
              navigate("/account/sign-in?deleted=1", { replace: true });
            } catch (e) {
              setError(errorMessage(e, t));
              setPending(false);
            }
          }}
        >
          <DialogBody className="flex flex-col gap-4">
            {blocked.length ? (
              <p className="flex items-start gap-2 rounded-lg border border-partial/40 bg-partial-bg px-3 py-2 text-sm text-partial-fg">
                <TriangleAlertIcon className="mt-0.5 size-4 shrink-0" />
                {t("account.deleteBlocked", { garages: blocked.join(", "), count: blocked.length })}
              </p>
            ) : null}
            {deleted.length ? (
              <p className="flex items-start gap-2 rounded-lg border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
                <Trash2Icon className="mt-0.5 size-4 shrink-0" />
                {t("account.deleteGarages", { garages: deleted.join(", "), count: deleted.length })}
              </p>
            ) : null}
            <Field label={t("account.deletePassword")} htmlFor="delete-password">
              <Input
                id="delete-password"
                type="password"
                autoFocus
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </Field>
            <FormError message={error} />
          </DialogBody>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
              {t("common.cancel")}
            </Button>
            <Button type="submit" variant="destructive" disabled={pending || blocked.length > 0}>
              {t("account.deleteConfirm")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/** /account/restricted: a disabled or deleted account can only sign out or be deleted. */
export function RestrictedPage() {
  const { t } = useTranslation();
  const { data: me, isPending } = useMe();
  const signOut = useSignOut();
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (isPending) return;
    if (!me) window.location.replace("/account/sign-in");
    else if (me.status === "active") window.location.replace("/");
  }, [me, isPending]);
  if (!me || me.status === "active") return null;

  const disabled = me.status === "disabled";
  return (
    <AuthCard
      title={disabled ? t("account.disabledTitle") : t("account.deletedTitle")}
      body={disabled ? t("account.disabledBody") : t("account.deletedBody")}
    >
      <div className="flex flex-col gap-3">
        <p className="flex items-center gap-2 text-sm text-muted-foreground">
          <BanIcon className="size-4 shrink-0" />
          {me.email}
        </p>
        <Button variant="outline" onClick={() => void signOut()}>
          {t("account.signOut")}
        </Button>
        <Button variant="destructive" onClick={() => setDeleting(true)}>
          <UserXIcon />
          {t("account.deleteConfirm")}
        </Button>
      </div>
      <DeleteAccountDialog open={deleting} onOpenChange={setDeleting} />
    </AuthCard>
  );
}
